/**
 * FOUNDING ACCESS — peças de venda compartilhadas pela página principal (`/` e `/founding`) e pela
 * página de parceiro (`/p/<slug>`).
 *
 * Checkout de verdade: chama POST /api/public/founding-checkout (Dashboard/server/public-routes.ts),
 * com o cartão tokenizado direto no navegador pela Pagar.me Tokens API (o número cru nunca passa pelo
 * nosso backend — mesmo padrão do self-service em my-billing-page.tsx).
 *
 * Preço, lote e vagas vêm SEMPRE da API (`getFoundingRemaining`), nunca de texto fixo. O admin ativa
 * lote novo pelo painel (`/admin` → Founding Access → "Novo lote") e a página troca sozinha, sem
 * deploy. Ver `person-billing.ts::activateNewFoundingLote`.
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, Loader2, Lock } from "lucide-react";
import { Chapter, loteOrdinalLabel } from "@/components/site/shared";
import {
  tokenizeCard,
  purchaseFounding,
  captureAttribution,
  getFoundingRemaining,
  APP_BASE_URL,
  type FoundingRemaining,
  type PartnerOffer,
} from "@/lib/api";
import { reaisComCentavos } from "@/lib/dinheiro";
import { autorizacaoComCondicao, ID_DAS_CONDICOES, PARTES_DA_AUTORIZACAO_CURTA } from "@/lib/autorizacao";

/**
 * Data da próxima onda de ativação. Valor de NEGÓCIO — muda a cada lote.
 * Mantenha em sincronia com `ATIVACAO_PREVISTA` em
 * `Dashboard/client/src/pages/aguardando-ativacao-page.tsx`: a pessoa vê a data aqui e de novo na
 * tela de espera, e as duas discordarem é pior do que não mostrar data nenhuma.
 */
export const ATIVACAO_PREVISTA = "18/10/2026";

/** Preço público, depois do lote Founding. Fato de negócio repetido na copy inteira. */
export const PRECO_PUBLICO = "R$230";

/**
 * Preço em texto ("R$139"), SEMPRE a partir de `remaining.priceCents` (vem de `plans.priceCents` —
 * o mesmo campo que `purchaseFoundingAccess` trava em `priceSnapshotCents` na hora da compra).
 * `remaining === null` (carregando, ou a consulta falhou) devolve "—", nunca um número chutado:
 * escrever R$139 na tela enquanto o banco cobra R$189 gera reclamação de cliente.
 */
export function formatPrecoAtual(remaining: FoundingRemaining | null): string {
  if (!remaining) return "—";
  return `R$${Math.round(remaining.priceCents / 100).toLocaleString("pt-BR")}`;
}

/** "Primeiro Lote"/"Segundo Lote"/... Enquanto a API não respondeu, assume o 1º (valor inicial de
 *  `founding_access_config.currentLote`): errar o rótulo por um request não tem risco de dinheiro. */
export function formatLoteLabel(remaining: FoundingRemaining | null): string {
  return loteOrdinalLabel(remaining?.loteNumber ?? 1);
}

// ============================================================================
// ESTADO DO LOTE — vagas reais, reconsultadas a cada 60 s
// ============================================================================
export type FoundingState = "aberta" | "esgotada";

export function useFoundingState(): { state: FoundingState; remaining: FoundingRemaining | null } {
  const [remaining, setRemaining] = useState<FoundingRemaining | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => getFoundingRemaining().then((r) => { if (!cancelled) setRemaining(r); });
    load();
    // A página fica aberta muito tempo na mesma aba (gente lendo FAQ, conferindo preço) e o
    // contador não pode ficar parado enquanto outras pessoas compram.
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // `?state=aberta|esgotada` só em build de desenvolvimento: em produção o branch some do bundle.
  if (import.meta.env.DEV) {
    const forced = new URLSearchParams(window.location.search).get("state");
    if (forced === "aberta" || forced === "esgotada") return { state: forced, remaining };
  }

  // Esgotada só quando a API confirma `remaining <= 0`, nunca por dedução. Se a API falhar, a
  // página não trava em "esgotada" por engano: continua vendendo e o servidor recusa se não houver vaga.
  if (remaining !== null && remaining.remaining <= 0) return { state: "esgotada", remaining };
  return { state: "aberta", remaining };
}

// ============================================================================
// CONTADOR DE VAGAS
// ============================================================================
export function SlotsBar({ remaining, style }: { remaining: FoundingRemaining | null; style?: React.CSSProperties }) {
  const loteLabel = formatLoteLabel(remaining);
  if (!remaining) {
    return (
      <div className="hts-slots" style={style}>
        <div className="hts-slots-label">
          <span>Vagas no {loteLabel}</span>
          <span>verificando…</span>
        </div>
        <div className="hts-slots-bar-track" />
      </div>
    );
  }
  const usadas = Math.max(0, remaining.totalSeats - remaining.remaining);
  const pct = remaining.totalSeats > 0 ? Math.min(100, Math.round((usadas / remaining.totalSeats) * 100)) : 0;
  return (
    <div className="hts-slots" style={style}>
      <div className="hts-slots-label">
        <span>Vagas no {loteLabel}</span>
        <strong>{remaining.remaining} de {remaining.totalSeats} restantes</strong>
      </div>
      <div className="hts-slots-bar-track">
        <div className="hts-slots-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ============================================================================
// TOPBAR SIMPLES — usada na página de parceiro (foco em não distrair do checkout)
// ============================================================================
export function Topbar({ ctaLabel = "Garantir vaga", onCTA, label = "Founding Access" }: { ctaLabel?: string; onCTA?: () => void; label?: string }) {
  const defaultCTA = () => document.getElementById("form")?.scrollIntoView({ behavior: "smooth" });
  return (
    <header className="lv2-topbar is-scrolled">
      <a href="/" className="lv2-topbar-logo">
        <span className="hts-wordmark">HTS</span>
      </a>
      <nav className="lv2-topbar-nav">
        <span className="nav-link" style={{ cursor: "default" }}>{label}</span>
        <button onClick={onCTA ?? defaultCTA} className="cta">{ctaLabel}</button>
      </nav>
    </header>
  );
}

// ============================================================================
// O QUE ESTÁ INCLUSO
// ============================================================================
const INCLUSO_GRUPOS: { categoria: string; itens: string[] }[] = [
  {
    categoria: "Dado",
    itens: [
      "Mercado à vista, futuro e índices da B3, em tempo real",
      "Índices globais, yields, moedas e commodities, em tempo real",
      "Sem taxa de market data cobrada por fora",
    ],
  },
  {
    categoria: "Research Quantitativo",
    itens: [
      "Estrutura completa da curva de juros e detecção de mudança de regime",
      "Inversão de curva",
      "Inclinação (slopes) em tempo real",
      "Índices sintéticos de juros, construídos pela HTS",
      "Valuation de ativos, recalculado continuamente",
      "Precificação de CPI, antes e depois da divulgação",
      "Expectativa de Fed e de Copom",
      "Classificação de regime de mercado",
      "Percepção de risco sistêmico e sentimento por ativo",
    ],
  },
  {
    categoria: "Dentro do terminal",
    itens: ["Feed de notícias", "Calendário econômico", "Vídeo de uso para cada ferramenta"],
  },
  {
    categoria: "Acesso",
    itens: ["Windows, Mac e navegador", "Celular via webapp"],
  },
];

export function IncludedSection({ id = "ch-001", number = "001" }: { id?: string; number?: string }) {
  return (
    <Chapter id={id} number={number} eyebrow="O Que Está Incluso" title={<>Um plano.<br /><span className="lv2-outlined">Todos os acessos.</span></>}>
      <div style={{ gridColumn: "1 / -1" }}>
        <div className="hts-included-grid">
          {INCLUSO_GRUPOS.map((grupo) => (
            <div key={grupo.categoria} className="hts-anchor-col">
              <div className="hts-anchor-head">{grupo.categoria}</div>
              <ul className="hts-anchor-list">
                {grupo.itens.map((item) => (
                  <li key={item}><span>{item}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p style={{ maxWidth: 700, margin: "40px auto 0", textAlign: "center", fontSize: 16, color: "#b8b8b8", lineHeight: 1.7 }}>
          Não existe plano superior, módulo pago à parte nem cobrança adicional de dado.
          O que está nessa lista é o que você recebe.
        </p>
      </div>
    </Chapter>
  );
}

// ============================================================================
// QUEM ESTÁ POR TRÁS (versão curta)
// ============================================================================
export function BehindSection() {
  return (
    <Chapter id="ch-005" number="005" eyebrow="Quem Está Por Trás" title={<>535 operadores<br /><span className="lv2-outlined">já usam</span> todo dia.</>}>
      <div style={{ gridColumn: "1 / -1" }}>
        <p style={{ maxWidth: 700, margin: "0 auto", fontSize: 16, color: "#b8b8b8", lineHeight: 1.75, textAlign: "center" }}>
          A HTS roda em produção desde 2021, em estrutura institucional completa — OMS, RMS e
          backoffice, com 535 operadores profissionais. Cinco anos de desenvolvimento, dois deles
          com a porta fechada, reconstruindo do zero.
          <br /><br />
          Desenvolvida e operada pela High Trading LTDA.
        </p>
      </div>
    </Chapter>
  );
}

// ============================================================================
// HELPERS — formatação leve, não é validação (isso o backend faz de verdade)
// ============================================================================
function formatCpf(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function formatPhone(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d{1,4})$/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d{1,4})$/, "$1-$2");
}
function formatCardNumber(v: string) {
  return v.replace(/\D/g, "").slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ");
}

// ============================================================================
// CHECKOUT FORM
// ============================================================================
type Step = "form" | "submitting" | "success-paid" | "success-trial" | "success-pending" | "error";

/** Dia da 1ª cobrança: hoje + os dias de teste, do jeito que o servidor conta. */
function primeiraCobranca(dias: number, agora = Date.now()): string {
  const d = new Date(agora);
  d.setDate(d.getDate() + dias);
  return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/** dd/mm/aaaa a partir do ISO que o checkout devolve. */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/**
 * `oferta`: o mesmo formulário vende pelo link de parceiro (`/p/<slug>`). Muda o preço exibido, o
 * texto do consentimento e o `partnerSlug` enviado — conta, cartão e desfecho são idênticos, porque
 * o cliente do parceiro entra na mesma fila de ativação da pré-venda.
 */
export function CheckoutForm({ remaining, oferta }: { remaining: FoundingRemaining | null; oferta?: PartnerOffer }) {
  // Oferta de parceiro com centavos, como o texto da condição ("R$ 99,90"): arredondado, a mesma tela mostrava dois preços.
  const preco = oferta ? reaisComCentavos(oferta.priceCents) : formatPrecoAtual(remaining);
  // Só acordo de parceiro tem teste. A pré-venda cobra na hora e ativa em ondas — ali o texto fixo
  // de founder continua certo.
  // Acordo com meta de lotes (18/10/2026): a entrada sem cobrança vale para qualquer pessoa (o servidor manda os 30
  // dias do 1º ciclo), então a tela não precisa saber quem compra.
  const diasDeTeste = oferta?.trialDays ?? 0;
  // `?preview=paid|pending` mostra as telas de desfecho sem comprar. Só em build de
  // desenvolvimento: em produção não há como forçar uma tela de "pagamento confirmado" falsa.
  const [step, setStep] = useState<Step>(() => {
    if (!import.meta.env.DEV) return "form";
    const preview = new URLSearchParams(window.location.search).get("preview");
    if (preview === "paid") return "success-paid";
    if (preview === "pending") return "success-pending";
    if (preview === "trial") return "success-trial";
    return "form";
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [fimDoTeste, setFimDoTeste] = useState("");
  // 01/10/2026 (frente HTS-Checkout-Ja-Tenho-Conta): quem já tem conta compra com ela — e-mail ou CPF + senha —,
  // sem recriar nada. O servidor confere a conta ANTES de cobrar. `faltando` = dados fiscais que a conta não tem:
  // a tela pede só esses, no lugar, e a mesma compra continua.
  const [modo, setModo] = useState<"novo" | "conta">("novo");
  const [login, setLogin] = useState("");
  const [faltando, setFaltando] = useState<string[]>([]);
  const [aviso, setAviso] = useState("");
  const [autoriza, setAutoriza] = useState(false);
  // O texto EXATO da autorização é o que vai gravado (com hash) antes da cobrança. A data da 1ª cobrança fica no
  // quadro da condição, logo acima; aqui a frase curta (dono, 01/10).
  // Com meta (18/10), o aceite grava a condição EXATA do servidor: benefício do acordo e acerto do ciclo no cancelamento.
  // Desde 20/10 o checkbox mostra só a frase curta (`lib/autorizacao.ts`); o texto enviado continua com a condição inteira.
  const textoDaAutorizacao = oferta?.condicao
    ? autorizacaoComCondicao(oferta.condicao)
    : `Autorizo a HTS a cobrar ${preco}/mês no cartão informado, de forma recorrente até eu cancelar.`;
  const pede = (campo: string) => modo === "novo" || faltando.includes(campo);
  // Dado fiscal que o servidor pediu fica em vermelho até estar preenchido (dono, 01/10).
  const falta = (campo: string, ok: boolean) => `hts-waitlist-input${faltando.includes(campo) && !ok ? " is-faltando" : ""}`;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Confirmação só existe no cliente. A senha é criada aqui numa tacada só, sem verificação por
  // e-mail: errar de dedo significaria pagar e não conseguir entrar na conta.
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  // Obrigatório: a Pagar.me recusa criar o cliente sem telefone, e a compra falha antes de tentar o
  // cartão — aparecendo pro usuário como "cartão não passou".
  const [phone, setPhone] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvv, setCvv] = useState("");

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrorMsg("");

      setAviso("");
      // Antes de tokenizar: o token é de uso único, e falhar depois dele faria a pessoa redigitar o cartão.
      if (!autoriza) {
        setStep("error");
        setErrorMsg("Marque a autorização da cobrança recorrente para continuar.");
        return;
      }
      if (modo === "novo" && password !== passwordConfirm) {
        setStep("error");
        setErrorMsg("As senhas não conferem. Confira e tenta de novo.");
        return;
      }

      setStep("submitting");

      const tokenRes = await tokenizeCard({ number: cardNumber, holderName, expMonth, expYear, cvv });
      if ("error" in tokenRes) {
        setStep("error");
        setErrorMsg(tokenRes.error);
        return;
      }

      const result = await purchaseFounding({
        modo,
        chaveDaCompra: crypto.randomUUID(),
        ...(modo === "conta" ? { login } : { email }),
        password,
        ...(pede("name") ? { name } : {}),
        ...(pede("cpf") ? { cpf } : {}),
        ...(pede("phone") ? { phone } : {}),
        cardToken: tokenRes.token,
        autorizaCobrancaRecorrente: true,
        textoDaAutorizacao,
        // O consentimento é o registro legal do que foi aceito: tem que descrever a condição
        // REAL. Com período de teste não há cobrança hoje nem espera de ativação, e prometer
        // "1º ciclo de 30 dias na ativação" a quem tem 152 dias de teste descreve outra venda.
        consentText: oferta
          ? diasDeTeste > 0
            ? oferta.metaLotes
              // Com meta, a regra só existe na condição (fonte única, gravada no aceite): aqui uma frase que remete a ela.
              ? `Comprei a HTS pela parceria ${oferta.partnerName}, na condição especial de custeio por volume (${oferta.metaLotes} contratos por ciclo) aceita na autorização desta compra.`
              : `Comprei a HTS pela parceria ${oferta.partnerName} — ${diasDeTeste} dias de teste sem cobrança, acesso liberado na hora, depois ${preco}/mês enquanto mantiver a condição da parceria.`
            : `Comprei a HTS pela parceria ${oferta.partnerName} — ${preco}/mês enquanto mantiver a condição da parceria, primeiro ciclo de 30 dias começa só na ativação.`
          : `Comprei o Founding Access da HTS — ${preco}/mês, primeiro ciclo de 30 dias começa só na ativação.`,
        partnerSlug: oferta?.slug,
        ...captureAttribution(),
      });

      if (!result.ok && result.codigo === "conta_existente") {
        // Já tem conta: troca para "já tenho conta" com o e-mail preenchido. Os campos do cartão ficam como estão;
        // o token é de uso único, e o reenvio gera outro.
        setModo("conta");
        setLogin(email);
        setPassword("");
        setStep("form");
        setAviso(result.error || "Você já tem conta na HTS. Entre com o seu e-mail e senha para continuar.");
        return;
      }
      if (!result.ok && result.codigo === "condicao_da_meta") {
        // A condição do escritório mudou desde que a página abriu: o servidor não abre a compra com o aceite antigo.
        setAutoriza(false);
        setStep("error");
        setErrorMsg(result.error || "A condição desta oferta foi atualizada. Recarregue a página, leia a condição e autorize de novo.");
        return;
      }
      if (!result.ok && result.codigo === "dados_fiscais") {
        setFaltando(result.faltando ?? []);
        setStep("form");
        setAviso(result.error || "Para concluir a compra, complete os dados abaixo.");
        return;
      }
      if (!result.ok) {
        setStep("error");
        // O backend manda o erro POR CAMPO (fieldErrors do zod). Sem mostrar isso, quem preenche
        // não tem como adivinhar qual dos 10 campos está errado.
        const porCampo = Object.values(result.fieldErrors ?? {}).flat().filter(Boolean);
        setErrorMsg(porCampo.length > 0 ? porCampo.join(" ") : result.error || "Não foi possível concluir a compra.");
        return;
      }
      if (result.paid && result.subscriptionStatus === "trialing") {
        setFimDoTeste(dataBR(result.trialEndsAt));
        setStep("success-trial");
        return;
      }
      setStep(result.paid ? "success-paid" : "success-pending");
    },
    [email, password, passwordConfirm, name, cpf, phone, cardNumber, holderName, expMonth, expYear, cvv, preco, oferta, diasDeTeste, modo, login, autoriza, textoDaAutorizacao, faltando]
  );

  // ── ENTROU NO TESTE: acesso liberado agora, nada cobrado ───────────────────────────────────
  //
  // Desfecho diferente da pré-venda e por isso tela própria. Quem compra um acordo com período de
  // teste já pode entrar: `iniciarPeriodoDeTeste` grava `trialing` E `activation_at`, que é o
  // campo que destrava o gate do app. Mandar essa pessoa para /aguardando-ativacao dizia que ela
  // estava numa fila em que não está — aconteceu com o primeiro cliente do Instituto Brasileiro
  // de Trade (23/09), que tinha 5 meses de teste e viu "ativação prevista para 18/10".
  if (step === "success-trial") {
    return (
      <div className="hts-result" data-cursor-hover>
        <div className="hts-result-bar">
          <span className="hts-result-eyebrow">{oferta ? `HTS · ${oferta.partnerName}` : "HTS"}</span>
          <span className="hts-result-status is-ok">
            <i aria-hidden="true" />
            Acesso liberado
          </span>
        </div>

        <div className="hts-result-body">
          <h3>Pronto — sua conta já está liberada.</h3>
          {oferta?.metaLotes ? (
            // Meta de lotes (18/10): só a ENTRADA é sem cobrança; o 1º ciclo é decidido pela meta, no fim e no cancelamento.
            <p>
              Nada foi cobrado na entrada. O 1º ciclo vai até <strong>{fimDoTeste || "o fim do ciclo"}</strong> e é decidido
              pela meta de {oferta.metaLotes} contratos{oferta.corretorasTexto ? ` em conta ${oferta.corretorasTexto}` : ""}, nas regras da condição
              que você aceitou, inclusive se cancelar.
            </p>
          ) : (
            <p>
              Nada foi cobrado: seu acesso começa agora e vai até <strong>{fimDoTeste || "o fim do período combinado"}</strong>.
              A primeira cobrança de <strong>{preco}</strong> sai nessa data, e você pode cancelar antes
              pela sua conta, sem pagar nada.
            </p>
          )}

          <dl className="hts-result-grid">
            <div>
              <dt>Acesso</dt>
              <dd>Liberado agora</dd>
            </div>
            <div>
              <dt>{oferta?.metaLotes ? "1º ciclo" : "Período de teste"}</dt>
              <dd>{diasDeTeste > 0 ? `${diasDeTeste} dias` : "Combinado"}</dd>
            </div>
            <div>
              <dt>{oferta?.metaLotes ? "Fim do 1º ciclo" : "1ª cobrança"}</dt>
              <dd>{fimDoTeste || (oferta?.metaLotes ? "No fim do ciclo" : "No fim do teste")}</dd>
            </div>
          </dl>

          <div className="hts-result-actions">
            <a href={APP_BASE_URL} className="lv2-cta-btn">
              Entrar na plataforma
              <ArrowUpRight size={18} />
            </a>
            <a href={`${APP_BASE_URL}/api/download/hts`} className="lv2-arrow-link">
              Baixar o HTS agora
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── PAGOU NA ENTRADA pelo link de um parceiro com teste (revisão de 18/10/2026) ────────────────────────────
  //
  // Quem tem fatura em aberto de assinatura anterior não entra sem cobrança: o servidor cobra o 1º ciclo na hora
  // (`diasDeTesteDaCompra`). Plano de parceiro não é Founding: o pagamento confirmado já libera o acesso, sem onda. A tela
  // de pago dizia "ativação em ondas" e mandava para /aguardando-ativacao. Com meta, esse ciclo fica pago e a meta
  // vale a partir do 2º (a mesma regra do texto da condição).
  if (step === "success-paid" && oferta && diasDeTeste > 0) {
    return (
      <div className="hts-result" data-cursor-hover>
        <div className="hts-result-bar">
          <span className="hts-result-eyebrow">HTS · {oferta.partnerName}</span>
          <span className="hts-result-status is-ok">
            <i aria-hidden="true" />
            Pagamento confirmado
          </span>
        </div>

        <div className="hts-result-body">
          <h3>Pronto — sua conta já está liberada.</h3>
          <p>
            {oferta.metaLotes ? (
              <>
                Como havia fatura em aberto de uma assinatura anterior, o 1º ciclo foi cobrado agora ({preco}) e fica pago:
                nada mais é cobrado por ele, nem se você cancelar. A meta de {oferta.metaLotes} contratos vale a partir do 2º ciclo.
              </>
            ) : (
              <>Como você já teve uma assinatura da HTS, o 1º ciclo ({preco}) foi cobrado agora. O acesso já está liberado.</>
            )}
          </p>

          <dl className="hts-result-grid">
            <div>
              <dt>Acesso</dt>
              <dd>Liberado agora</dd>
            </div>
            <div>
              <dt>1º ciclo</dt>
              <dd>Pago</dd>
            </div>
            <div>
              <dt>{oferta.metaLotes ? "Meta de contratos" : "Pagamento"}</dt>
              <dd>{oferta.metaLotes ? "A partir do 2º ciclo" : "Confirmado"}</dd>
            </div>
          </dl>

          <div className="hts-result-actions">
            <a href={APP_BASE_URL} className="lv2-cta-btn">
              Entrar na plataforma
              <ArrowUpRight size={18} />
            </a>
            <a href={`${APP_BASE_URL}/api/download/hts`} className="lv2-arrow-link">
              Baixar o HTS agora
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success-paid" || step === "success-pending") {
    const pago = step === "success-paid";
    return (
      <div className="hts-result" data-cursor-hover>
        <div className="hts-result-bar">
          <span className="hts-result-eyebrow">{oferta ? `HTS · ${oferta.partnerName}` : "Founding Access"}</span>
          <span className={`hts-result-status ${pago ? "is-ok" : "is-pending"}`}>
            <i aria-hidden="true" />
            {pago ? "Pagamento confirmado" : "Pagamento pendente"}
          </span>
        </div>

        <div className="hts-result-body">
          <h3>{pago ? "Sua vaga está garantida." : "Sua vaga está reservada."}</h3>
          <p>
            {pago ? (
              <>
                Pagamento confirmado e conta criada. A ativação acontece em ondas — a sua está
                prevista para <strong>{ATIVACAO_PREVISTA}</strong>, e o ciclo de 30 dias só começa
                a partir dela.
              </>
            ) : (
              <>
                A vaga é sua e ninguém a ocupa — mas o cartão não foi aprovado. Atualize os dados
                de pagamento para confirmar sua entrada na próxima onda.
              </>
            )}
          </p>

          <dl className="hts-result-grid">
            <div>
              <dt>Ativação prevista</dt>
              <dd>{pago ? ATIVACAO_PREVISTA : "Após confirmação"}</dd>
            </div>
            <div>
              <dt>Primeiro ciclo</dt>
              <dd>30 dias</dd>
            </div>
            <div>
              <dt>Pagamento</dt>
              <dd>{pago ? "Confirmado" : "Recusado"}</dd>
            </div>
          </dl>

          <div className="hts-result-actions">
            <a href={pago ? `${APP_BASE_URL}/aguardando-ativacao` : `${APP_BASE_URL}/minha-conta/billing`} className="lv2-cta-btn">
              {pago ? "Ir pra minha conta" : "Atualizar cartão"}
              <ArrowUpRight size={18} />
            </a>
            {pago && (
              <a href={`${APP_BASE_URL}/api/download/hts`} className="lv2-arrow-link">
                Baixar o HTS agora
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className="hts-checkout-form" onSubmit={handleSubmit} id="form">
      <div className="hts-checkout-abas" role="tablist">
        <button type="button" role="tab" aria-selected={modo === "novo"} className={modo === "novo" ? "is-ativa" : ""} onClick={() => { setModo("novo"); setFaltando([]); setAviso(""); }}>Sou novo</button>
        <button type="button" role="tab" aria-selected={modo === "conta"} className={modo === "conta" ? "is-ativa" : ""} onClick={() => { setModo("conta"); setAviso(""); }}>Já tenho conta</button>
      </div>
      {aviso && <div className="hts-checkout-aviso">{aviso}</div>}

      <div className="hts-checkout-section">
        <div className="hts-checkout-section-title">{modo === "conta" ? "Sua conta HTS" : "Seus dados"}</div>
        <div className="hts-checkout-grid">
          {modo === "conta" ? (
            <>
              <input type="text" required autoComplete="username" placeholder="E-mail ou CPF da sua conta" value={login} onChange={(e) => setLogin(e.target.value)} className="hts-waitlist-input" />
              <input type="password" required autoComplete="current-password" placeholder="Sua senha" value={password} onChange={(e) => setPassword(e.target.value)} className="hts-waitlist-input" />
              <a className="hts-checkout-esqueci" href={`${APP_BASE_URL}/?recuperar=1`} target="_blank" rel="noopener noreferrer">Esqueci a senha</a>
            </>
          ) : null}
          {pede("name") && <input type="text" required placeholder="Nome completo" value={name} onChange={(e) => setName(e.target.value)} className={falta("name", name.trim().length >= 2)} />}
          {pede("cpf") && <input type="text" required placeholder="CPF" value={cpf} onChange={(e) => setCpf(formatCpf(e.target.value))} className={falta("cpf", cpf.replace(/\D/g, "").length === 11)} />}
          {pede("phone") && <input type="tel" required placeholder="Celular com DDD" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} className={falta("phone", [10, 11].includes(phone.replace(/\D/g, "").length))} />}
          {modo === "novo" && (
            <>
              <input type="email" required placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="hts-waitlist-input" />
              <input type="password" required minLength={8} autoComplete="new-password" placeholder="Crie uma senha (mín. 8 caracteres)" value={password} onChange={(e) => setPassword(e.target.value)} className="hts-waitlist-input" />
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Confirme a senha"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="hts-waitlist-input"
                aria-invalid={passwordConfirm.length > 0 && passwordConfirm !== password}
              />
            </>
          )}
        </div>
      </div>

      <div className="hts-checkout-section">
        <div className="hts-checkout-section-title">
          <Lock size={13} /> Pagamento — cartão de crédito
        </div>
        <div className="hts-checkout-grid">
          <input
            type="text"
            required
            inputMode="numeric"
            placeholder="Número do cartão"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            className="hts-waitlist-input"
            style={{ gridColumn: "1 / -1" }}
          />
          <input type="text" required placeholder="Nome impresso no cartão" value={holderName} onChange={(e) => setHolderName(e.target.value)} className="hts-waitlist-input" style={{ gridColumn: "1 / -1" }} />
          <input type="text" required inputMode="numeric" maxLength={2} placeholder="MM" value={expMonth} onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, ""))} className="hts-waitlist-input" />
          <input type="text" required inputMode="numeric" maxLength={4} placeholder="AAAA" value={expYear} onChange={(e) => setExpYear(e.target.value.replace(/\D/g, ""))} className="hts-waitlist-input" />
          <input type="text" required inputMode="numeric" maxLength={4} placeholder="CVV" value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, ""))} className="hts-waitlist-input" />
        </div>
      </div>

      {/* A condição, do lado de fora da letra miúda (23/09). O cartão é pedido na hora mesmo no
          teste — quem não lê "hoje você não paga nada" aqui acha que está sendo cobrado agora, e
          isso vira chargeback e chamado. Mesma linguagem do checkout novo, de propósito: é a mesma
          venda em duas telas, e divergir no texto é divergir na promessa. */}
      {diasDeTeste > 0 && (
        <div className="hts-checkout-condicao">
          {oferta?.metaLotes ? (
            // Meta de lotes (18/10): não é teste. A regra (fim do ciclo, cancelamento, desconto) só existe na condição, a
            // fonte única que o aceite grava (revisão de 18/10): aqui só a entrada, a data e a remissão a ela.
            <>
              <strong>Entre sem pagar nada hoje</strong>, com acesso liberado na hora.
              <br />
              O 1º ciclo, de {diasDeTeste} dias, termina em <strong>{primeiraCobranca(diasDeTeste)}</strong> e é decidido pela meta
              de {oferta.metaLotes} contratos, nas regras das <a href={`#${ID_DAS_CONDICOES}`} style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 2 }}>condições da oferta</a>, no fim da página.
              {modo === "conta" && (
                // Mesma exceção do checkout do Dashboard (`aviso-entrada-com-meta`) e do servidor (`diasDeTesteDaCompra`).
                <>
                  <br />
                  <strong>Já teve conta?</strong> Você também entra sem pagar, salvo fatura em aberto de assinatura anterior
                  (veja "Assinaturas anteriores" na condição).
                </>
              )}
            </>
          ) : (
            <>
              <strong>Hoje você não paga nada.</strong> São {diasDeTeste} dias de teste, com acesso liberado na hora.
              <br />
              A 1ª cobrança de {preco} é em <strong>{primeiraCobranca(diasDeTeste)}</strong>, e depois todo mês. Cancelando antes, não é cobrado.
            </>
          )}
        </div>
      )}

      <label className="hts-checkout-autoriza">
        <input type="checkbox" checked={autoriza} onChange={(e) => setAutoriza(e.target.checked)} required />
        {/* O link abre e mostra a condição no rodapé da página (o Footer abre o bloco ao clicar). */}
        <span>{oferta?.condicao ? (
          <>
            {PARTES_DA_AUTORIZACAO_CURTA.antes}
            <a href={`#${ID_DAS_CONDICOES}`} style={{ color: "#e5e5e5", textDecoration: "underline", textUnderlineOffset: 2 }}>
              {PARTES_DA_AUTORIZACAO_CURTA.link}
            </a>
            {PARTES_DA_AUTORIZACAO_CURTA.depois}
          </>
        ) : textoDaAutorizacao}</span>
      </label>

      <button type="submit" className="lv2-cta-btn hts-checkout-submit" disabled={step === "submitting" || !autoriza} data-cursor-hover>
        {step === "submitting" ? (
          <><Loader2 size={16} className="hts-spin" /> Processando...</>
        ) : oferta?.metaLotes ? (
          modo === "conta" ? <>Entrar com a minha conta <ArrowUpRight size={18} /></> : <>Entrar sem pagar hoje <ArrowUpRight size={18} /></>
        ) : diasDeTeste > 0 ? (
          <>Começar meu teste de {diasDeTeste} dias <ArrowUpRight size={18} /></>
        ) : (
          <>Garantir minha vaga — {preco}/mês <ArrowUpRight size={18} /></>
        )}
      </button>

      {step === "error" && <div className="hts-waitlist-error">{errorMsg}</div>}
    </form>
  );
}
